import { Word } from '@/common/types/scopeAndSequence';
import { CreateAttempt } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import { playSentenceAudio } from '@/common/utils/audio';
import { sanitize } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getWordById,
  getWordBySpelling,
  getWordsByIds,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getSentenceBuilder,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { SentenceBuilderCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import {
  CardFeedback,
  KeyboardFeedback,
} from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { calculateProgress } from '@/game/gameHelpers';
import SingleCharacterText from '@/game/prefabs/StudentGame/SingleCharacterText';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';

export class SentenceBuilderController extends MinigameController {
  override startRound() {
    const isRoundEnded = getGeneralGameState().isRoundEnded;
    if (isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }

    this.handleSetSentenceToBuild();
    this.updateRules(
      this.minigame.getControlScheme(getStudent().settings.controllerType),
    );
  }

  async handleSetSentenceToBuild() {
    const sentenceBuilderState = getSentenceBuilder();
    const { gameData } = getGeneralGameState();
    const currentSentencesList = gameData.sentenceBuilder;

    if (!currentSentencesList) {
      console.error('No sentences found');
      return;
    }

    const filteredSentenceList = currentSentencesList.filter((sentence) => {
      return !sentenceBuilderState.usedSentences.some(
        (usedSentence) => usedSentence.spelling === sentence.spelling,
      );
    });

    const sentenceToBuild =
      filteredSentenceList[
        Math.floor(Math.random() * filteredSentenceList.length)
      ];

    GameInstance.instance.updateSentenceBuilder({
      sentenceToBuild: sentenceToBuild,
    });
    const sentenceWords = getWordsByIds(sentenceToBuild.words);
    const shuffledSentenceCards = sentenceWords
      .map((word) => ({
        id: uuidv4(),
        word,
      }))
      .sort(() => Math.random() - 0.5);

    GameInstance.instance.updateSentenceBuilder({
      initialShuffledSentence: shuffledSentenceCards,
      shuffledSentenceToBuild: shuffledSentenceCards,
    });
    try {
      await playSentenceAudio(sentenceToBuild.spelling);
    } catch (error) {
      console.error('Error saying sentence', error);
    }
  }

  override async playInstructions() {
    const sentenceToBuild = getSentenceBuilder().sentenceToBuild;
    if (!sentenceToBuild) {
      console.error('No sentence to build');
      return;
    }

    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Replay word button clicked', {
        word: sentenceToBuild.spelling,
      });
    }

    await playSentenceAudio(sentenceToBuild.spelling);
  }

  override async checkMinigameAnswer() {
    const student = getStudent();
    const sentenceBuilderState = getSentenceBuilder();

    const studentAttempt = this.minigame.getInput(' ').trim();
    if (!studentAttempt.length) {
      console.error('No student attempt');
      return;
    }

    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity ID found');
      return;
    }

    const sentenceToBuild = sentenceBuilderState.sentenceToBuild;
    if (!sentenceToBuild) {
      console.error('No sentence to build');
      return;
    }

    function punctuationCorrect(studentAttempt: string): boolean {
      return (
        !studentAttempt.substring(0, studentAttempt.length - 1).includes('.') &&
        studentAttempt[0] === studentAttempt[0].toUpperCase() &&
        studentAttempt.endsWith('.') !== false
      );
    }

    // Push the attempt into the session.
    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'sentencebuilder',
      correctAnswer: sentenceToBuild.spelling,
      correctAnswerId: sentenceToBuild.id,
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
      studentAnswer: studentAttempt,
      cardsPlayed: this.minigame.getBoardCards().map((card) => card.cardText),
    };
    GameInstance.instance.addAttempt(attemptData);

    // Did the player get the sentence correct?
    const sentenceCorrect: boolean =
      studentAttempt.toLowerCase() ===
        sentenceToBuild.spelling.toLowerCase() + '.' &&
      punctuationCorrect(studentAttempt);

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(sentenceCorrect);

    // Handle sentence completion
    if (sentenceCorrect) {
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: true,
      });
      await this.sentenceCompleted();
    } else {
      if (!punctuationCorrect(studentAttempt)) {
        // Show incorrect punctuation feedback.
        this.minigame.highlightRules();
      } else {
        await this.showFeedback(this.getItemFeedback(), {
          correctAnswer: false,
          partiallyCorrectAnswer:
            this.getIsPartiallyCorrectAnswer(studentAttempt),
          forceIndividualFeedback: true,
          forceReadout: true,
        });
      }
    }

    // Update the progress.
    const updatedProgress = calculateProgress(
      GameInstance.instance.generalGameState,
      GameInstance.instance.sentenceBuilder,
      100,
    );
    GameInstance.instance.updateGeneralGameState({
      progress: updatedProgress,
    });

    if (sentenceCorrect) {
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
    }
  }

  returnWordToShuffledSentence(wordId: string) {
    // Action payload is the id of the incorrectly guessed item
    const state = getSentenceBuilder();
    const attemptItemId = wordId;
    const attemptItem = state.studentAttempt.find(
      (item) => item.id === attemptItemId,
    );
    if (attemptItem) {
      // Add incorrect letter to shuffledWordToBuild
      const newShuffledSentenceToBuild = [
        ...state.shuffledSentenceToBuild,
        attemptItem,
      ];
      const studentAttempt = getSentenceBuilder().studentAttempt.map((item) => {
        if (item?.id === attemptItem.id)
          return {
            id: uuidv4(),
            word: {
              id: 'word-0',
              spelling: '_',
              morphemes: [],
              definition: '',
              example: '',
            } as Word,
          };
        return item;
      });
      GameInstance.instance.updateSentenceBuilder({
        shuffledSentenceToBuild: newShuffledSentenceToBuild,
        studentAttempt,
      });
    }
  }

  async sentenceCompleted() {
    const sentenceToBuild = getSentenceBuilder().sentenceToBuild;
    if (!sentenceToBuild) {
      console.error('No sentence to build');
      return;
    }
    GameInstance.instance.updateSentenceBuilder({
      usedSentences: [...getSentenceBuilder().usedSentences, sentenceToBuild],
    });
  }

  override createCardList(minigameScene: Minigame): {
    dealtCards: StudentGameCard[];
    boardCards: StudentGameCard[];
  } {
    const sentenceCards = getSentenceBuilder().initialShuffledSentence.map(
      (card: SentenceBuilderCard) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(newCard, card, 'hand');
        return newCard;
      },
    );
    const shuffledCards = sentenceCards.sort(() => Math.random() - 0.5);
    return {
      dealtCards: shuffledCards,
      boardCards: [],
    };
  }

  override getMaxBoardCards(): number {
    return getSentenceBuilder().initialShuffledSentence.length;
  }

  override getShouldDisplayBoardSlots(): boolean {
    return true;
  }

  async doWordFeedback(
    index: number,
    length: number,
    feedbackType: 'correct' | 'incorrect' | 'neutral',
  ) {
    const letters = this.minigame
      .getPlayerTypingInputs()[0]
      ?.getCharacterArray();
    if (!letters) {
      console.error('No letters found');
      return;
    }
    for (var i = index; i < index + length; i++) {
      const letter = letters[i] as SingleCharacterText;
      if (!letter) continue;
      if (feedbackType === 'correct') {
        letter.doSuccessFeedback();
      } else if (feedbackType === 'incorrect') {
        letter.doIncorrectFeedback();
      } else if (feedbackType === 'neutral') {
        letter.doNeutralFeedback();
      }
    }
    await waitForSeconds(this.minigame.time, 0.25);
  }

  override handleMinigameCardDropped(data: {
    card: StudentGameCard;
    dropSiteName: string;
    targetIndex: number;
  }) {
    // Update punctuation of all the cards.
    super.handleMinigameCardDropped(data);
    const cards = this.minigame.getAllCards();
    cards.forEach((card) => {
      const cardParent = card.origin?.dropSite;
      if (cardParent?.siteName !== 'board') {
        // Revert the card's punctuation.
        card.setCardText(card.cardData?.word?.spelling || '');
      } else {
        // Set the card's punctuation based on index.
        const cardText = card.cardData?.word?.spelling;
        if (!cardText) return;
        const firstIndex = 0;
        const lastIndex =
          (this.minigame.getDropSiteFromCard(card)?.layoutGroup?.data
            .maxCards || 0) - 1;
        if (card.origin?.index === firstIndex) {
          const capitalizedCardText =
            cardText.charAt(0).toUpperCase() + cardText.slice(1);
          card.setCardText(capitalizedCardText);
        } else if (card.origin?.index === lastIndex) {
          const fullstopCardText = cardText + '.';
          card.setCardText(fullstopCardText);
        } else {
          // Revert the card's punctuation.
          card.setCardText(card.cardData?.word?.spelling || '');
        }
      }
      this.minigame.getBoardDropSite().updateLayout();
      this.minigame.getHandDropSite().updateLayout();
    });
  }

  override getCardFeedback(): CardFeedback[] {
    const boardState = this.minigame.getBoardDropSite().layoutGroup;
    if (!boardState) {
      console.error('No board state found');
      return [];
    }

    const cards = boardState.getMinigameCards() as StudentGameCard[];
    const sentenceToBuild = getSentenceBuilder().sentenceToBuild;

    return cards.map((card, i) => ({
      card,
      isCorrect:
        card.cardData?.word?.spelling ===
        getWordById(sentenceToBuild!.words[i])?.spelling,
    }));
  }

  override getKeyboardFeedback(): KeyboardFeedback[] {
    const sentenceToBuild = getSentenceBuilder().sentenceToBuild;
    if (!sentenceToBuild) {
      console.error('No sentence to build');
      return [];
    }

    const studentAttempt = this.minigame.getTypedInput().split(' ');
    const inputChars = this.minigame
      .getPlayerTypingInputs()[0]
      .getCharacterArray();

    const correctWords = getWordsByIds(sentenceToBuild.words);
    let currIndex = 0;

    return studentAttempt.map((studentWord, i) => {
      const correctWord = correctWords[i]?.spelling;

      // Handle case where student typed more words than expected
      if (!correctWord) {
        const letterTexts = inputChars.slice(
          currIndex,
          currIndex + studentWord.length,
        ) as SingleCharacterText[];
        currIndex += studentWord.length + 1;
        return {
          letters: letterTexts,
          isCorrect: false,
          word: getWordBySpelling(sanitize(studentWord)),
        };
      }

      // Remove trailing punctuation for comparison
      const studentWordClean = studentWord.replace(/[.,!?;:]$/, '');
      const hasPunctuation = studentWord.length > studentWordClean.length;

      // Check spelling is correct
      let isCorrect =
        studentWordClean.toLowerCase() === correctWord.toLowerCase();

      // Check first word is capitalized
      if (i === 0 && studentWordClean.length > 0) {
        const firstChar = studentWordClean[0];
        isCorrect = isCorrect && firstChar === firstChar.toUpperCase();
      }

      // Check last word has full stop
      if (i === correctWords.length - 1) {
        isCorrect = isCorrect && hasPunctuation && studentWord.endsWith('.');
      } else {
        // Non-last words should not have punctuation
        isCorrect = isCorrect && !hasPunctuation;
      }

      const studentWordObj = getWordBySpelling(sanitize(studentWordClean));
      const letterTexts = inputChars.slice(
        currIndex,
        currIndex + studentWord.length,
      ) as SingleCharacterText[];
      currIndex += studentWord.length + 1; // + 1 for the space

      return {
        letters: letterTexts,
        isCorrect,
        word: studentWordObj,
      };
    });
  }

  override canShowArticulatoryGestures(): boolean {
    return false;
  }

  override canShowWordSegmentation(): boolean {
    return false;
  }

  override async readFinalAnswer() {
    const sentenceToBuild = getSentenceBuilder().sentenceToBuild;
    if (!sentenceToBuild) return;
    await playSentenceAudio(sentenceToBuild.spelling);
  }

  // Update rules based on the control scheme.
  override updateRules(scheme: ControllerType) {
    this.minigame.setRulesVisible(scheme === ControllerType.KEYBOARD);

    if (scheme !== ControllerType.KEYBOARD) {
      return;
    }

    let studentAttempt = this.minigame.getInput(' ');
    if (!studentAttempt.length) {
      this.minigame.setRulesStatus([false, false]);
      return;
    }

    const firstWord = studentAttempt[0];
    const startsWithCapital = firstWord[0] === firstWord[0].toUpperCase();

    const lastWord = studentAttempt[studentAttempt.length - 1];
    const endsWithFullStop = lastWord.endsWith('.');

    this.minigame.setRulesStatus([startsWithCapital, endsWithFullStop]);
  }

  getIsPartiallyCorrectAnswer(studentAttempt: string) {
    const sentenceToBuild = getSentenceBuilder().sentenceToBuild;
    if (!sentenceToBuild) {
      console.error('No sentence to build');
      return false;
    }

    const attemptInSentence = sanitize(sentenceToBuild.spelling).includes(
      sanitize(studentAttempt),
    );

    const attemptAtStart =
      sanitize(sentenceToBuild.spelling).indexOf(sanitize(studentAttempt)) ===
      0;
    return attemptInSentence && attemptAtStart;
  }
}
